// Module location : libellés, formats (heure de Paris), statuts et petits composants partagés.
import type { ReactNode } from "react";
import { Badge, StatusChip, type CheckStatus } from "@core/ui";
import { money } from "@core/format";

export type CampaignStatus = "brouillon" | "ouverte" | "selection" | "attribuee" | "close";
export type AppStatus = "brouillon" | "deposee" | "a_completer" | "en_verification" | "verifiee" | "retenue" | "refusee" | "liste_attente" | "retiree";

export const CAMPAIGN_STATUS_LABELS: Record<CampaignStatus, string> = {
  brouillon: "Brouillon",
  ouverte: "Ouverte aux candidatures",
  selection: "Sélection en cours",
  attribuee: "Attribuée",
  close: "Close",
};

export const APP_STATUS_LABELS: Record<AppStatus, string> = {
  brouillon: "Brouillon (non déposée)",
  deposee: "Déposée",
  a_completer: "À compléter",
  en_verification: "En vérification",
  verifiee: "Contrôles effectués",
  retenue: "Retenue",
  refusee: "Non retenue",
  liste_attente: "Liste d’attente",
  retiree: "Retirée par le candidat",
};

export const REGIMES = [
  { value: "vide", label: "Location vide" },
  { value: "meuble", label: "Location meublée" },
  { value: "colocation", label: "Colocation" },
  { value: "mobilite", label: "Bail mobilité" },
  { value: "etudiant", label: "Bail étudiant (meublé, 9 mois)" },
  { value: "commercial", label: "Bail commercial" },
  { value: "professionnel", label: "Bail professionnel" },
];

export const regimeLabel = (v: string) => REGIMES.find((r) => r.value === v)?.label ?? v;

export const SITUATIONS = [
  { value: "cdi", label: "CDI" },
  { value: "cdd", label: "CDD" },
  { value: "interim", label: "Intérim" },
  { value: "fonctionnaire", label: "Fonctionnaire" },
  { value: "independant", label: "Indépendant" },
  { value: "etudiant", label: "Étudiant" },
  { value: "alternant", label: "Alternant" },
  { value: "retraite", label: "Retraité" },
  { value: "sans_emploi", label: "Sans emploi" },
  { value: "autre", label: "Autre situation" },
];

export const CHECK_STATUS_ORDER: CheckStatus[] = ["conforme", "a_confirmer", "anomalie", "manquant", "non_concluant", "info"];

/** Badge d'état d'une campagne : mot + tonalité (jamais la couleur seule). */
export function CampaignBadge({ status, label }: { status: string; label?: string }) {
  const tone = status === "ouverte" ? "succes" : status === "selection" ? "encre" : status === "attribuee" ? "encre" : "neutre";
  return <Badge tone={tone}>{label ?? CAMPAIGN_STATUS_LABELS[status as CampaignStatus] ?? status}</Badge>;
}

/** Statut de candidature : ce n'est pas un résultat de contrôle, d'où un badge distinct des statuts de constat. */
export function AppStatusBadge({ status, label }: { status: string; label?: string }) {
  const tone =
    status === "retenue" ? "succes" : status === "a_completer" ? "attention" : status === "deposee" || status === "en_verification" || status === "verifiee" ? "encre" : "neutre";
  return <Badge tone={tone}>{label ?? APP_STATUS_LABELS[status as AppStatus] ?? status}</Badge>;
}

/** Décompte de constats par statut, sans total pondéré. */
export function CheckCounts({ counts, empty = "Aucun contrôle effectué" }: { counts: Record<string, number>; empty?: string }) {
  const entries = CHECK_STATUS_ORDER.filter((s) => counts?.[s]);
  if (!entries.length) return <span className="muted small">{empty}</span>;
  return (
    <span className="location-chips">
      {entries.map((s) => (
        <StatusChip key={s} status={s} label={`${statusWord(s)} · ${counts[s]}`} />
      ))}
    </span>
  );
}

function statusWord(s: CheckStatus): string {
  return { conforme: "Conforme", a_confirmer: "À confirmer", anomalie: "Anomalie détectée", manquant: "Pièce manquante", info: "Information", non_concluant: "Non concluant" }[s];
}

export { bpLabel, hoursLabel, parisDateTime, parisLocalToIso } from "./paris";

/** Loyer, charges et total charges comprises. */
export function rentLine(rent: number, charges: number, mode?: string): string {
  const c = charges ? ` + ${money(charges)} de charges${mode === "forfait" ? " (forfait)" : mode === "provision" ? " (provision)" : ""}` : "";
  return `${money(rent)} hors charges${c}`;
}

export function Kpi({ label, value, hint }: { label: string; value: ReactNode; hint?: ReactNode }) {
  return (
    <div className="stat">
      <span className="stat-label">{label}</span>
      <span className="stat-value num">{value}</span>
      {hint ? <span className="stat-hint">{hint}</span> : null}
    </div>
  );
}

/** Pièces justificatives admises (décret n° 2015-1437), présentées au candidat avant tout dépôt. */
export const LEGAL_PIECES: { title: string; items: string[] }[] = [
  { title: "Identité", items: ["Une pièce d’identité en cours de validité (carte nationale d’identité, titre de séjour ou autre titre officiel avec photographie)."] },
  { title: "Domicile actuel", items: ["Un justificatif selon votre situation : quittances de loyer, attestation d’hébergement, avis de taxe foncière…"] },
  { title: "Activité professionnelle", items: ["Selon votre situation : contrat de travail, attestation employeur, extrait K ou D1, carte d’étudiant, certificat de scolarité…"] },
  { title: "Ressources", items: ["Dernier ou avant-dernier avis d’imposition, trois derniers bulletins de salaire, justificatifs de bourse, de pension ou de prestations…"] },
];

export const FORBIDDEN_PIECES = "Aucune pièce interdite ne vous sera demandée : ni relevé de compte, ni photo d’identité, ni carte Vitale, ni attestation de bonne tenue de compte, ni dossier médical.";

export const CHECKS_EXPLAINED =
  "Votre dossier est vérifié avec les pièces que vous fournissez : lisibilité, dates, cohérence entre les pièces et, lorsqu’il existe, le code 2D-DOC. Vous voyez les mêmes constats que le bailleur et pouvez corriger une pièce. Le bailleur décide ; aucune décision n’est prise automatiquement.";
